// File upload related types

export type FileStatus = 'pending' | 'uploading' | 'uploaded' | 'failed';

// Supported MIME types (matching backend)
export const SUPPORTED_DOCUMENT_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
] as const;

export const SUPPORTED_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
] as const;

export const SUPPORTED_MIME_TYPES = [
  ...SUPPORTED_DOCUMENT_TYPES,
  ...SUPPORTED_IMAGE_TYPES,
] as const;

// Max file sizes in bytes
export const MAX_DOCUMENT_SIZE = 50 * 1024 * 1024; // 50 MB
export const MAX_IMAGE_SIZE = 20 * 1024 * 1024; // 20 MB

// API Request/Response types
export interface FileUploadUrlRequest {
  filename: string;
  mimeType: string;
  conversationId?: string; // Optional - files can be uploaded without a conversation
  sizeBytes?: number;
}

export interface FileUploadUrlResponse {
  fileId: string;
  uploadUrl: string;
  downloadUrl: string;
  expiresAt: string;
}

export interface FileConfirmResponse {
  fileId: string;
  status: 'uploaded';
  sizeBytes: number;
  downloadUrl: string;
}

export interface FileInfo {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number | null;
  status: 'pending' | 'uploaded' | 'failed';
  createdAt: string;
  downloadUrl?: string;
}

// Local file state (before/during upload)
export interface AttachedFile {
  id: string; // Temp ID (nanoid) or fileId after upload
  file: File; // Original File object
  filename: string;
  mimeType: string;
  sizeBytes: number;
  status: FileStatus;
  progress: number; // 0-100
  fileId?: string; // Server-assigned file ID (after upload)
  downloadUrl?: string; // For preview after upload
  error?: string; // Error message if failed
}

// Structured input content types
export interface InputTextContent {
  type: 'input_text';
  text: string;
}

export interface InputFileContent {
  type: 'input_file';
  fileId: string;
}

export type InputContent = InputTextContent | InputFileContent;
